import React, { useState } from 'react';
import { Eye, EyeOff, ArrowRight, Lock, Mail } from 'lucide-react';
import { ReactComponent as TraceLogo } from '../assets/trace-logo.svg';

interface LoginProps {
  onLogin: (email: string, password: string) => Promise<void>;
}

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('admin@nimq.io');
  const [password, setPassword] = useState('trace2026');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);
    try {
      await onLogin(email, password);
    } catch (err: any) {
      setError(err?.message ?? 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-50 flex">
      {/* Left Panel — Brand / Hero */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[560px] relative overflow-hidden flex-col justify-between p-10 bg-gradient-to-br from-brand-950 via-brand-900 to-brand-800">
        {/* Decorative gradient orbs */}
        <div className="absolute top-[-120px] right-[-80px] w-[400px] h-[400px] rounded-full bg-brand-400/10 blur-[100px]" />
        <div className="absolute bottom-[-60px] left-[-60px] w-[300px] h-[300px] rounded-full bg-brand-300/8 blur-[80px]" />
        <div className="absolute top-[40%] left-[60%] w-[200px] h-[200px] rounded-full bg-brand-400/5 blur-[60px]" />

        {/* Logo */}
        <div className="relative z-10 flex justify-center">
          <TraceLogo className="h-24 w-auto [&_path]:fill-white [&_.cls-1]:fill-white [&_.cls-2]:fill-white [&_.cls-3]:fill-white [&_.cls-4]:fill-white" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex flex-col justify-center -mt-10">
          <h1 className="text-2xl font-extrabold text-white leading-tight tracking-tight mb-4 text-center whitespace-nowrap">
            TRACE AI-Powered Compliance Evaluation
          </h1>
          <p className="text-brand-200/70 text-sm leading-relaxed max-w-[360px] text-center mx-auto">
            Intelligent text recognition and agent-driven compliance evaluation for any industry.
            Upload reports, documents, or files — and let TRACE AI handle the rest.
          </p>

          {/* Feature highlights */}
          <div className="mt-10 space-y-4 max-w-[360px] mx-auto">
            {[
              { label: 'Smart Document Analysis', desc: 'TRACE AI-powered text extraction & compliance review' },
              { label: 'Industry Agnostic', desc: 'Works across regulatory frameworks & standards' },
              { label: 'Continuous Learning', desc: 'Adaptive model improvement with every evaluation' },
            ].map((feature, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-400 mt-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white/90">{feature.label}</p>
                  <p className="text-xs text-brand-200/50 mt-0.5">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className="relative z-10">
          <p className="text-[11px] text-brand-200/30 font-medium">
            NimQ &middot; Secure Compliance Platform &middot; v0.0.1
          </p>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="lg:hidden mb-10">
            <TraceLogo className="h-8 w-auto" />
          </div>

          {/* Welcome text */}
          <div className="mb-8">
            <h2 className="text-2xl font-extrabold text-surface-900 tracking-tight">
              Welcome back
            </h2>
            <p className="text-sm text-surface-500 mt-1.5">
              Sign in to your TRACE account to continue
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-accent-red/8 border border-accent-red/15">
                <div className="w-5 h-5 rounded-full bg-accent-red/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-accent-red text-xs font-bold">!</span>
                </div>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider block mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@company.com"
                  className="input-field pl-10"
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider">
                  Password
                </label>
                <button
                  type="button"
                  className="text-[11px] font-medium text-brand-400 hover:text-brand-500 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="Enter your password"
                  className="input-field pl-10 pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => setRememberMe(!rememberMe)}
                className={`w-4.5 h-4.5 rounded-md border-2 flex items-center justify-center transition-all duration-200
                  ${rememberMe
                    ? 'bg-brand-400 border-brand-400'
                    : 'bg-white border-surface-300 hover:border-surface-400'
                  }`}
                style={{ width: '18px', height: '18px' }}
              >
                {rememberMe && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 3.5L3.5 6L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              <span className="text-sm text-surface-600">Remember me</span>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2.5 py-3 px-5 rounded-xl text-sm font-semibold
                bg-brand-400 text-white hover:bg-brand-500 active:bg-brand-600
                transition-all duration-200 shadow-sm hover:shadow-md
                disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-brand-400
                group"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                    <path d="M12 2a10 10 0 019.95 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" />
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign in</span>
                  <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-7">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-surface-200" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-surface-50 px-3 text-surface-400 font-medium">or continue with</span>
            </div>
          </div>

          {/* SSO Options */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
                bg-white text-surface-700 border border-surface-200 hover:bg-surface-50 hover:border-surface-300
                transition-all duration-200"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M15.68 8.18c0-.567-.05-1.113-.145-1.636H8v3.094h4.305a3.68 3.68 0 01-1.597 2.415v2.007h2.585C14.68 12.82 15.68 10.71 15.68 8.18z" fill="#4285F4" />
                <path d="M8 16c2.16 0 3.97-.716 5.293-1.94l-2.585-2.008c-.716.48-1.633.764-2.708.764-2.083 0-3.846-1.407-4.476-3.298H.86v2.073A7.997 7.997 0 008 16z" fill="#34A853" />
                <path d="M3.524 9.518A4.81 4.81 0 013.273 8c0-.527.09-1.04.25-1.518V4.41H.861A7.997 7.997 0 000 8c0 1.29.31 2.512.86 3.59l2.664-2.072z" fill="#FBBC05" />
                <path d="M8 3.184c1.174 0 2.229.403 3.058 1.196l2.294-2.294C11.966.79 10.157 0 8 0A7.997 7.997 0 00.86 4.41l2.664 2.072C4.154 4.59 5.917 3.184 8 3.184z" fill="#EA4335" />
              </svg>
              Google
            </button>
            <button
              type="button"
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
                bg-white text-surface-700 border border-surface-200 hover:bg-surface-50 hover:border-surface-300
                transition-all duration-200"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
              </svg>
              GitHub
            </button>
          </div>

          {/* Sign up link */}
          <p className="text-center text-sm text-surface-500 mt-8">
            Don't have an account?{' '}
            <button type="button" className="font-semibold text-brand-400 hover:text-brand-500 transition-colors">
              Request access
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
